import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Entidad } from '../../../../data/Entidad';
import { CFacturaFolioEstado, IFacturaDetalleFolioFactura } from '../../../../data/entidad/Factura';
import { MFinanzaEdoCtaMovimientoDetalle } from '../../../../data/model/FinanzaEstadoCuenta/MFinanzaEdoCtaMovimientoDetalle';
import { _SvFacturaObtenerDetalleFolioFactura } from '../../../../data/modulo/FacturaCFDI';
import { _DICC_ALUMNO_MOVPAGO_FOLIO } from '../../../../data/modulo/FinanzasEstadoCuenta';
import _L from '../../../../util/Labels';
import '../../../components/Icon';
import { _ModalSimple } from "../../../controlD3/ModalThings/ModalSimple";
import { ModalThingsUtil } from '../../../controlD3/ModalThings/Utils';
import '../../../controlWC/FileIcoComponent';
import '../../../controlWC/TooltipComponent';
import { UIUtilFormat } from '../../../util/Format';
import { UIUtilTime } from '../../../util/Time';
import { UIUtilViewData } from '../../../util/ViewData';
import { AlumnoEdoCtaFacturacionUtil } from '../AlumnoEdoCtaFacturacionForm/FacturacionUtil';
import { AlumnoEdoCtaUtil } from '../AlumnoEdoCtaUtil';
import ic_download from '/image/iconos/ic_download.svg?raw';
import ic_eye from '/image/iconos/ic_eye_v2.svg?raw';

import CCategoriaMovimiento = Entidad.CFinanzaCategoriaMovimiento;


export interface AlumnoEdoCtaMovDetallePagoModalOptions {
    Alumno: Entidad.IAlumno;
    MovDetalle: MFinanzaEdoCtaMovimientoDetalle;
    OnClickVerOtrosCargos?: () => void;
}


export function AlumnoEdoCtaMovDetallePagoModal(options: AlumnoEdoCtaMovDetallePagoModalOptions) {
    const { MovDetalle: movDetalle } = options;
    if (![CCategoriaMovimiento.PagoCompleto, CCategoriaMovimiento.PagoParcial].includes(movDetalle.Categoria)) {
        console.warn("AlumnoEdoCtaMovDetallePagoModal: El detalle no es de tipo pago completo o parcial");
        return;
    }

    const folioPago = _DICC_ALUMNO_MOVPAGO_FOLIO.get(movDetalle.FolioPago);
    const esPagoOnlinePendiente = movDetalle._IsPagoOnlinePendiente();

    _ModalSimple({
        Title: (() => {
            let strText = UIUtilViewData._GetStr_CategoriaMovimiento(movDetalle.Categoria);
            if (folioPago) {
                if (esPagoOnlinePendiente) {
                    strText += ` (${_L("general.pendiente").toLowerCase()})`;
                }
                strText += ` - ${folioPago.Folio}`;
            }
            return strText;
        })(),
        Width: 500,
        DrawContent: (content, mt) => {
            const renderContent = new EdoCtaPaymentDetailComponent();

            content.node().appendChild(renderContent);

            renderContent.k_options = {
                alumno: options.Alumno,
                movDetalle: options.MovDetalle,
                onClickVerOtrosCargos: options.OnClickVerOtrosCargos,
                mt,
            };

        },
    })
}


// ****************************************************************
// Component: EdoCtaPaymentDetailComponent
// ****************************************************************

enum PaymentDetailComponentLoadState {
    NA = 0,
    Loading = 1,
    SuccessLoaded = 2,
    LoadError = 3
}

@customElement('app-edocta-payment-detail')
class EdoCtaPaymentDetailComponent extends LitElement {
    @property({ type: Object }) k_options: {
        alumno: Entidad.IAlumno
        movDetalle: MFinanzaEdoCtaMovimientoDetalle
        onClickVerOtrosCargos?: () => void
        mt: ModalThingsUtil.IModalThings
    } = null;

    private _detalleFactura: IFacturaDetalleFolioFactura | null = null;

    // para forzar re-render cuando se actualicen datos de factura
    @state() private k_invoiceDetailLoadState: PaymentDetailComponentLoadState = PaymentDetailComponentLoadState.NA;

    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM
    }

    protected render(): TemplateResult {
        if (!this.k_options) return null;

        const { alumno, movDetalle } = this.k_options;
        if (![CCategoriaMovimiento.PagoCompleto, CCategoriaMovimiento.PagoParcial].includes(movDetalle.Categoria)) {
            console.warn("AlumnoEdoCtaMovDetallePagoModal: El detalle no es de tipo pago completo o parcial");
            return null;
        }

        return html`
        <div class="alumnoedocta_modaldetallepago_container">
            <p>
                <b>${_L("panelfinanzasedocuenta.d_field_descripcion")}:</b>
                <span>${movDetalle.ParentMovimiento.Cargo}</span>
            </p>
            <p>
                <b>${_L("general.tipo")}:</b>
                <span>${UIUtilViewData._GetStr_CategoriaMovimiento(movDetalle.Categoria)}</span>
            </p>
            <p>
                <b>${_L("panelfinanzasedocuenta.d_field_pagoabono")}:</b>
                <span>${UIUtilFormat._CurrencyFmt(movDetalle.Valor)}</span>
            </p>
            <p>
                <b>${_L("general.fechapago")}:</b>
                <span>${movDetalle.FechaAplicacion ? UIUtilTime._DateFormatStandarFixTimeZoneByIdSchool(movDetalle.FechaAplicacion, alumno.IdKinder, "dd/mm/yyyy h12:mm") : _L("general.pendiente")}</span>
            </p>
            <p>
                <b>${_L("panelfinanzasedocuenta.d_field_metodopago")}:</b>
                <span>${AlumnoEdoCtaUtil._GetNombreMetodoPago(movDetalle.IdMetodoPago)}</span>
            </p>


            ${this.renderOnlinePaymentInfo()}


            ${this.renderFacturaInfo()}

        </div>`;
    }

    private renderOnlinePaymentInfo(): TemplateResult {
        const { alumno, movDetalle, onClickVerOtrosCargos, mt } = this.k_options;
        const tieneCargosRelacionados = movDetalle.PagosMovRelacionados && movDetalle.PagosMovRelacionados.length > 0;
        const folioPago = movDetalle.FolioPagoEnLineaRelacionado;
        const esPagoOnlinePendiente = movDetalle._IsPagoOnlinePendiente();
        const clickVerOtrosCargos = tieneCargosRelacionados && !!onClickVerOtrosCargos
            ? (mt: ModalThingsUtil.IModalThings) => {
                mt.Modal._Ocultar();
                onClickVerOtrosCargos()
            }
            : undefined;
        const fnValidateToday = (dt1: string) => UIUtilTime._GetValidatorDateYMDV2(new Date(dt1)) === UIUtilTime._GetValidatorDateYMDV2(new Date());

        if (!folioPago) return null;

        return html`
        <fieldset>
            <legend>
                ${_L("panelfinanzasedocuenta.modaldetallepago.info_online_payment")}
            </legend>

            <p>
                <b>${_L("panelfinanzasedocuenta.modaldetallepago.folio")}:</b>
                <span>${folioPago.Folio}</span>
            </p>

            <p>
                <b>${_L("general.tipo")}:</b>
                <span>${movDetalle.MetodoPagoDetalle}</span>
            </p>

            <p>
                <b>${_L("general.estado")}:</b>
                <span style="color: ${esPagoOnlinePendiente ? "var(--color_app_orange2)" : "var(--color-info)"};">
                    ${esPagoOnlinePendiente ? _L("general.pendiente") : _L("general.success")}
                </span>
            </p>

            <p>
                <b>${_L("general.fecharegistro")}:</b>
                <span>${UIUtilTime._DateFormatStandarFixTimeZoneByIdSchool(folioPago.FechaCreacion, alumno.IdKinder, "dd/mm/yyyy h12:mm")}</span>
            </p>

            ${esPagoOnlinePendiente && folioPago.FechaVigencia
                ? html`
            <p>
                <b>${_L("panelfinanzasedocuenta.modaldetallepago.fecha_vencimiento")}:</b>
                <span style="color: ${fnValidateToday(folioPago.FechaVigencia) ? "var(--color_app_red1)" : "var(--color_app_orange2)"};">
                    ${UIUtilTime._DateFormatStandarFixTimeZoneByIdSchool(folioPago.FechaVigencia, alumno.IdKinder)}
                </span>
            </p>`
                : null}

            <p>
                <b>${_L("general.total")}:</b>
                <span>${UIUtilFormat._CurrencyFmt(folioPago.Montos.reduce((acc, monto) => acc + monto, 0))}</span>
            </p>

            <p>
                <b>
                    ${_L("panelfinanzasedocuenta.modaldetallepago.otros_cargos_rels")}
                    ${clickVerOtrosCargos
                ? html`<a href="javascript:void(0);" @click=${() => clickVerOtrosCargos(mt)}>(${_L("panelfinanzasedocuenta.modaldetallepago.ver_movs")})</a>`
                : null}
                    :
                </b>
                <div>
                    ${(() => (tieneCargosRelacionados
                ? movDetalle.PagosMovRelacionados.map(d => html`<div>- ${d.ParentMovimiento.Cargo}: ${UIUtilFormat._CurrencyFmt(d.Valor)}</div>`)
                : html`<i>${_L("panelfinanzasedocuenta.modaldetallepago.no_hay_otros_cargos_rels")}</i>`
            ))()}
                </div>
            </p>

        </fieldset>`
    }

    private renderFacturaInfo(): TemplateResult {
        const { movDetalle } = this.k_options;
        const folioPago = _DICC_ALUMNO_MOVPAGO_FOLIO.get(movDetalle.FolioPago);
        const incluyeInfoFactura = !!folioPago?.RequiereFactura || movDetalle.IdFacturaFolio != 0; // Mostrar info factura: si el pago en línea requiere factura o si el movimiento tiene folio de factura asociado (en caso de no ser pago en línea)

        if (!incluyeInfoFactura) return null;

        const estadoColor = {
            "-1": "var(--color_app_red1)", // folio de factura con error
            "0": "var(--color_app_orange2)", // sin folio de factura asociado (pendiente)
            "1": "var(--color-info)", // folio de factura exitosa
        }[movDetalle.IdFacturaFolio < 0 ? "-1" : movDetalle.IdFacturaFolio > 0 ? "1" : "0"];

        const estadoText = {
            "-1": _L("panelfinanzasedocuenta.tag_factura_fallo"),
            "0": _L("general.pendiente"),
            "1": _L("panelfinanzasedocuenta.tag_facturado"),
        }[movDetalle.IdFacturaFolio < 0 ? "-1" : movDetalle.IdFacturaFolio > 0 ? "1" : "0"];

        return html`
        <fieldset>
            <legend>
                ${_L("panelfinanzasedocuenta.modaldetallepago.info_factura")}
            </legend>

            <!-- CFDI: Estado de factura -->

            <p>
                <b>${_L("general.estado")}:</b>
                <span style="color: ${estadoColor};">
                    ${estadoText}
                </span>
            </p>

            <!-- CFDI: Detalles de factura (si tiene folio de factura asociado) -->
            ${this.renderFacturaInfoDetails()}

            <!-- CFDI: Archivos XML/PDF (si aplica) -->
            ${this.renderFacturaInfoFiles()}

        </fieldset>`
    }

    private renderFacturaInfoDetails(): TemplateResult | null {
        if (this.k_invoiceDetailLoadState == PaymentDetailComponentLoadState.Loading) {
            return html`<p>${_L("general.loading_details")}... <wc-spinner dim="8px" border-width="2px"></wc-spinner></p>`;
        }
        if (this.k_invoiceDetailLoadState == PaymentDetailComponentLoadState.LoadError) {
            return html`<p style="color: var(--color_app_red1);">${_L("general.notif_fail_infosync")}</p>`;
        }
        if (this.k_invoiceDetailLoadState == PaymentDetailComponentLoadState.SuccessLoaded) {
            const detalle = this._detalleFactura;
            const fecha = detalle?.FinAsignacion || detalle?.Creacion;
            const isSuccessful = detalle?.Estado === CFacturaFolioEstado.Exitoso;
            const alumno = this.k_options.alumno;

            return html`
                ${!isSuccessful ? html`
                <p>
                    <b>${_L("general.descripcion")}:</b>
                    ${detalle?.MensajeApi || _L("general.nodisponible")}
                </p>`
                    : null
                }
                <p>
                    <b>${_L("general.fecha")}:</b>
                    ${fecha ? UIUtilTime._DateFormatStandarFixTimeZoneByIdSchool(fecha, alumno.IdKinder, "dd/mm/yyyy h12:mm") : _L("general.nodisponible")}
                </p>

                ${isSuccessful ? html`
                <p>
                    <b>${_L("factura.uid")}:</b>
                    ${detalle?.UID || _L("general.nodisponible")}
                </p>
                <p>
                    <b>${_L("factura.uuid")}:</b>
                    ${detalle?.UUID || _L("general.nodisponible")}
                </p>
                ` : null}
            `;
        }
        return null;
    }

    private renderFacturaInfoFiles(): TemplateResult | null {
        const { alumno, movDetalle, mt } = this.k_options;

        if (movDetalle.IdFacturaFolio <= 0) return null;

        return html`
        <p>
            <b>${_L("panelfinanzasedocuenta_factura.descargar_doc")}:</b>
            <div class="cfdi_file_icons_container">
                ${(["xml", "pdf"] as const).map(ext => html`
                    <div>
                        <wc-fileico
                            ext=${ext.toUpperCase()}
                            style="width:58px; height:70px; cursor:pointer;"
                        ></wc-fileico>

                        <div class="cfdi_file_actions_container">
                            ${ext == "pdf" ? html`
                            <button
                                class="btn btn_round btn_ver"
                                @click=${() => AlumnoEdoCtaFacturacionUtil._VerArchivoCFDI(alumno.IdKinder, movDetalle.IdFacturaFolio, ext)}>
                                <app-icon .svg="${ic_eye}" .size=${"15px"} .color=${"var(--color_secondary4)"}></app-icon>
                                <wc-tooltip position="top">${_L("c_actions.ver")}</wc-tooltip>
                            </button>`: null}

                            <button
                                class="btn btn_round btn_descargar"
                                @click=${() => AlumnoEdoCtaFacturacionUtil._DescargarCFDI(alumno.IdKinder, movDetalle.IdFacturaFolio, ext, mt.Progress.node())}>
                                <app-icon .svg="${ic_download}" .size=${"15px"} .color=${"var(--color_secondary4)"}></app-icon>
                                <wc-tooltip position="top">${_L("general.descargar")}</wc-tooltip>
                            </button>
                        </div>
                    </div>`)}
            </div>
            <button
                class="bg_light UI_Button"
                @click=${() => AlumnoEdoCtaFacturacionUtil._DescargarCFDIsZip(null, alumno.IdKinder, [movDetalle.IdFacturaFolio], mt.Progress.node())}>
                ${_L("general.descargartodo")}
            </button>
        </p>`;
    }


    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has("k_options")) {
            const { movDetalle } = this.k_options;
            if (movDetalle.IdFacturaFolio && movDetalle.IdFacturaFolio != 0) {
                this.k_invoiceDetailLoadState = PaymentDetailComponentLoadState.Loading;
                this.consultarDetalleFactura();
            } else {
                this._detalleFactura = null;
                this.k_invoiceDetailLoadState = PaymentDetailComponentLoadState.NA;
            }
        }
    }

    private consultarDetalleFactura() {
        const idFolio = Math.abs(this.k_options.movDetalle.IdFacturaFolio);
        _SvFacturaObtenerDetalleFolioFactura(idFolio).then(res => {
            if (res.Resultado > 0) {
                this._detalleFactura = res.Datos;
                this.k_invoiceDetailLoadState = PaymentDetailComponentLoadState.SuccessLoaded;
            }
            else {
                console.error("Error al obtener detalle de factura:", res.Mensaje);
                this.k_invoiceDetailLoadState = PaymentDetailComponentLoadState.LoadError;
            }
        }).catch(err => {
            console.error("Error al obtener detalle de factura:", err);
            this.k_invoiceDetailLoadState = PaymentDetailComponentLoadState.LoadError;
        });
    }
}